import { useEffect, useState } from "react";
import { dataCurta, type Trilha } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { ModoOnibus } from "../componentes/EstadoOffline.js";
import { Icone } from "../componentes/Icone.js";
import { IconeSair } from "../componentes/icones.js";
import { Contador, Interruptor, SeletorSegmentado } from "../componentes/Controles.js";
import { useSessao } from "../estado/sessao.js";
import { useTrilhaAtiva, useTrilhas } from "../estado/trilhas.js";
import { limparTudo } from "../offline/fila.js";
import { aplicarTema, temaGuardado, type Tema } from "../tema.js";
import { definirTamanho, tamanhoDaConta, type TamanhoDoTexto } from "../tamanho-do-texto.js";

/**
 * Ajustes — tela própria desde a Etapa 4 da identidade visual.
 *
 * **Ela saiu da home, e a home ficou só ritual.** Tema, fuso, modo férias,
 * modo ônibus e sair estavam empilhados no pé do início, embaixo do heatmap.
 * São coisas de momentos diferentes: o início é a tela que se abre todo dia
 * para saber o que estudar, e ajuste é o que se mexe uma vez por mês. Um
 * embaixo do outro, o ajuste não estorva de fato, mas faz a tela do dia
 * terminar num depósito — e era isso que sobrava depois que a faixa de
 * métricas e o heatmap já tinham dito tudo.
 *
 * Nada foi perdido na mudança: são os mesmos controles, no mesmo estado, um
 * destino adiante. A conta continua sendo um link daqui, porque exportação e
 * passkey são de outra ordem de raridade ainda.
 */
/**
 * Os cinco cartões de tema do 9j. A Lamparina ainda está em discussão (o
 * handoff do Caderno a mostra só como miniatura), então os dois dela ficam
 * desabilitados até o tema existir.
 */
const TEMAS: readonly { nome: string; valor: Tema | null; classe: string }[] = [
  { nome: "Caderno claro", valor: "claro", classe: "caderno-claro" },
  { nome: "Caderno escuro", valor: "escuro", classe: "caderno-escuro" },
  { nome: "Lamparina escura", valor: null, classe: "lamparina-escura" },
  { nome: "Lamparina clara", valor: null, classe: "lamparina-clara" },
  { nome: "Seguir o aparelho", valor: "sistema", classe: "aparelho" },
];

export function Ajustes({ aoSair }: { aoSair: () => void }) {
  const sessao = useSessao();
  const ativa = useTrilhaAtiva();
  const { recarregar } = useTrilhas();
  const [tema, setTema] = useState<Tema>(temaGuardado());
  const [tamanho, setTamanho] = useState<TamanhoDoTexto>(() => tamanhoDaConta(sessao.usuario.id));
  const [mudandoFerias, setMudandoFerias] = useState(false);

  function trocarTema(novo: Tema) {
    setTema(novo);
    aplicarTema(novo);
  }

  function trocarTamanho(novo: TamanhoDoTexto) {
    setTamanho(novo);
    definirTamanho(sessao.usuario.id, novo);
  }

  const emFerias = ativa?.feriasAtivo ?? false;


  return (
    <div className="tela tela--painel">
      <div className="tela__corpo">
        {/* Linhas de ajuste: texto à esquerda, controle à direita (ADR 039). */}
        <section className="ajustes__grupo" aria-labelledby="tema-titulo">
          <h2 className="t-secao" id="tema-titulo">
            Aparência
          </h2>
          <p className="t-corpo t-forte" id="rotulo-tema">
            Tema
          </p>
          <div className="temas" role="radiogroup" aria-labelledby="rotulo-tema">
            {TEMAS.map((t) => {
              const marcado = t.valor !== null && tema === t.valor;
              return (
                <button
                  key={t.nome}
                  type="button"
                  role="radio"
                  aria-checked={marcado}
                  disabled={t.valor === null}
                  className={marcado ? "tema-cartao tema-cartao--marcado" : "tema-cartao"}
                  title={t.valor === null ? "Em breve" : undefined}
                  onClick={() => t.valor && trocarTema(t.valor)}
                >
                  <span className={`tema-cartao__amostra tema-cartao__amostra--${t.classe}`} aria-hidden="true">
                    <span className="tema-cartao__aa">Aa</span>
                    {t.classe === "aparelho" && <span className="tema-cartao__aa tema-cartao__aa--2">Aa</span>}
                    <span className="tema-cartao__traco" />
                  </span>
                  <span className={marcado ? "t-anotacao tema-cartao__nome marca-texto" : "t-anotacao tema-cartao__nome"}>
                    {t.nome}
                  </span>
                  {t.valor === null && <span className="so-leitor"> (em breve)</span>}
                </button>
              );
            })}
          </div>

          <p className="t-corpo t-forte ajustes__rotulo" id="rotulo-tamanho">
            Tamanho do texto
          </p>
          <div className="fila-mao" role="radiogroup" aria-labelledby="rotulo-tamanho">
            {(["normal", "grande"] as const).map((t) => (
              <button key={t} type="button" role="radio" aria-checked={tamanho === t} className="chip-mao" onClick={() => trocarTamanho(t)}>
                {t}
              </button>
            ))}
          </div>
          <p className="ajustes__previa">
            <span className="t-anotacao t-2">fica assim:</span>{" "}
            <span className={tamanho === "grande" ? "ajustes__previa-texto ajustes__previa-texto--grande" : "ajustes__previa-texto"}>
              Seja A = {"{1, 2, 3}"}. Quantos elementos tem P(A) × A?
            </span>
          </p>
        </section>

        <section className="ajustes__grupo ajustes__estudo" aria-labelledby="estudo-titulo">
          <h2 className="t-secao" id="estudo-titulo">
            Estudo
          </h2>

          {ativa && (
            <div className="linha-ajuste">
              <Interruptor
                rotulo="Modo férias"
                ajuda="Congela a ofensiva sem quebrar a sequência: os dias em férias não contam como falha nem como cumprimento."
                ligado={emFerias}
                desabilitado={mudandoFerias}
                aoMudar={(ligar) => {
                  setMudandoFerias(true);
                  // A trilha é a fonte da verdade do modo férias; recarregar o
                  // estado compartilhado é o que faz a home e esta tela
                  // concordarem sem uma segunda cópia do dado.
                  void pedir("trilha.ferias", { params: { id: ativa.id }, corpo: { ativo: ligar } })
                    .then(() => recarregar())
                    .finally(() => setMudandoFerias(false));
                }}
              />
            </div>
          )}

          {ativa && <TetoDeRevisoes trilha={ativa} />}

          <ModoOnibus />
        </section>
      </div>
    </div>
  );
}

/**
 * A conta, que saiu das preferências para a aba Conta (ADR 039): quem está
 * entrando, em que fuso o dia vira, e sair.
 */
export function DadosDaConta({ aoSair }: { aoSair: () => void }) {
  const sessao = useSessao();
  const [saindo, setSaindo] = useState(false);
  return (
    <section className="ajustes__grupo conta-dados" aria-labelledby="conta-titulo">
      <h2 className="t-secao" id="conta-titulo">
        Conta
      </h2>
      <dl className="conta-dados__lista">
        <div className="conta-dados__linha">
          <dt className="t-anotacao t-2">fuso do dia</dt>
          <dd className="t-corpo">{sessao.usuario.fusoHorario}</dd>
        </div>
        <div className="conta-dados__linha">
          <dt className="t-anotacao t-2">hoje, para você</dt>
          <dd className="t-corpo">{dataCurta(sessao.hoje)}</dd>
        </div>
        <div className="conta-dados__linha">
          <dt className="t-anotacao t-2">entrando como</dt>
          <dd className="t-corpo">{sessao.usuario.email}</dd>
        </div>
      </dl>
      <p className="t-ajuda">O dia vira à meia-noite desse fuso. A meta e a ofensiva seguem ele.</p>
      <div className="conta-dados__sair">
        <button
          className="botao botao--secundario"
          type="button"
          onClick={() => {
            setSaindo(true);
            void pedir("sessao.sair")
              .finally(() => limparTudo())
              .finally(aoSair);
          }}
          disabled={saindo}
        >
          <Icone glifo={IconeSair} papel="acao" />
          {saindo ? "Saindo…" : "Sair"}
        </button>
      </div>
    </section>
  );
}


/**
 * O teto de revisões por dia (Fase 5).
 *
 * Ele existe porque o SM-2 devolve no mesmo dia tudo o que foi respondido no dia
 * anterior: no banco de produção, 97 respostas num dia viraram uma fila de 173
 * de 174 itens. Uma fila desse tamanho não é uma fila, é um muro — e quem olha
 * um muro não estuda nenhum tijolo.
 *
 * **O excedente não é adiado.** Ele continua vencido, com a data original, e
 * reaparece amanhã: o teto é de exibição, não de agendamento. Adiar no banco
 * apagaria o atraso, que é o dado que ordena a fila.
 *
 * Grava ao sair do campo, e não a cada tecla: digitar "45" passaria por "4",
 * que é um teto de verdade e uma gravação inútil.
 */
function TetoDeRevisoes({ trilha }: { trilha: Trilha }) {
  const { aplicar } = useTrilhas();
  const [valor, setValor] = useState(String(trilha.maximoRevisoesDia));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => setValor(String(trilha.maximoRevisoesDia)), [trilha.maximoRevisoesDia]);

  // Grava um instante depois do último toque, e não a cada um: tocar "+" cinco
  // vezes seriam cinco gravações da trilha inteira.
  useEffect(() => {
    if (Number(valor) === trilha.maximoRevisoesDia) return;
    const t = setTimeout(() => void gravar(), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  async function gravar() {
    const n = Number(valor);
    if (!Number.isInteger(n) || n < 1) {
      setValor(String(trilha.maximoRevisoesDia));
      return;
    }
    if (n === trilha.maximoRevisoesDia) return;
    setSalvando(true);
    setErro(null);
    try {
      /*
       * A edição manda a trilha **inteira**, porque `trilha.editar` recebe
       * `zTrilhaEntrada` e não um remendo. Mandar só o teto zeraria os outros
       * campos pelos padrões do schema — e seria a forma mais silenciosa de
       * apagar a data-alvo de alguém.
       */
      const r = await pedir("trilha.editar", {
        params: { id: trilha.id },
        corpo: {
          nome: trilha.nome,
          iniciais: trilha.iniciais,
          descricao: trilha.descricao ?? "",
          metaDiaria: trilha.metaDiaria,
          dataAlvo: trilha.dataAlvo,
          diasDeDescanso: trilha.diasDeDescanso,
          reincidenciaAlfa: trilha.reincidenciaAlfa,
          reincidenciaBeta: trilha.reincidenciaBeta,
          maximoRevisoesDia: n,
        },
      });
      aplicar(r);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível salvar o teto.");
      setValor(String(trilha.maximoRevisoesDia));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="linha-ajuste linha-ajuste--teto">
      <div className="linha-ajuste__texto">
        <label className="linha-ajuste__rotulo" htmlFor="teto-revisoes">
          Máximo de revisões por dia
        </label>
        <span className="linha-ajuste__ajuda">
          A fila para nesse número, com as mais atrasadas primeiro. O que sobra aparece nos dias seguintes.
        </span>
        {erro && (
          <p className="campo__erro" role="alert">
            {erro}
          </p>
        )}
      </div>
      {/* O campo grava sozinho um instante depois do último toque. */}
      <span className="campo-com-sufixo">
        <input
          id="teto-revisoes"
          className="campo__entrada campo__entrada--numero"
          type="number"
          inputMode="numeric"
          min={1}
          max={1000}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
        <span className="t-anotacao t-2">por dia</span>
      </span>
      {salvando && <span className="so-leitor" role="status">Salvando</span>}
    </div>
  );
}
